import { FacilityId, ProgramId, TimetableId } from '@gyym/sdk'
import { useGyymClient } from '@gyym/sdk-react'
import { useModal, useSnackbar } from '@gyym/ui'
import { useRouter } from 'next/router'
import React from 'react'

import { ReservationModal } from '@/components/Modals/ReservationModal'
import { pushContxCvEvent } from '@/lib/contx'
import { siteUrl } from '@/lib/env'
import { pushReservationEvent } from '@/lib/googleTagManager'
import { useAuthUser, useResendVerificationEmail } from '@/modules/Auth'
import { useToggleFavorite } from '@/modules/Favorites'
import {
  generatePointPurchaseUrl,
  generateShortagePointPurchaseUrl,
} from '@/modules/Me'
import { buildProgramLink } from '@/modules/Programs'
import { getImageUrl } from '@/utils/getImageUrl'

const OGP_IMAGE_URL = `${siteUrl}/assets/images/ogp.png`

const useRetUrl = (
  programId: ProgramId | undefined,
  timetableId: TimetableId | undefined,
) => {
  const router = useRouter()
  // Build return url
  const basePath = router.asPath.split('?')[0]
  const query = new URLSearchParams()
  if (programId) {
    query.set('programId', programId.toString())
  }
  if (timetableId) {
    query.set('timetableId', timetableId.toString())
  }
  query.set('purchased', 'true')

  return `${basePath}?${query.toString()}`
}

export const useTimetableReserve = (
  facility: {
    id: FacilityId
    cityCode: number
    prefectureId: number
  },
  referral:
    | 'facility'
    | 'program'
    | 'online_facility'
    | 'online_program'
    | 'favorites',
  onReserve?: (timetableId: TimetableId) => void | Promise<void>,
  // Program thumbnail (relative path) for the contx cart item; the timetable
  // detail the modal fetches does not include it
  getProgramImage?: (programId: ProgramId) => string | undefined,
) => {
  const [loadingTimetableId, setLoadingTimetableId] = React.useState<
    TimetableId | undefined
  >(undefined)
  const [isOpen, setOpen] = React.useState(false)
  const {
    authUser,
    isProfileRegistered,
    isLoggedIn,
    isEmailVerified,
    revalidate,
  } = useAuthUser()
  const { openSnackbar } = useSnackbar()
  const router = useRouter()
  const client = useGyymClient()
  const { openModal } = useModal()
  const resendVerificationEmail = useResendVerificationEmail(
    // TODO: Temporarily set intended return path to TOP
    // Should replace to the current path after migrating all pages to app router using next/navigation
    '/',
  )

  const programId = router.query.programId
    ? Number(router.query.programId)
    : undefined
  const timetableId = router.query.timetableId
    ? Number(router.query.timetableId)
    : undefined

  const purchased = router.query.purchased === 'true'

  const retUrl = useRetUrl(programId, timetableId)

  const { isFavorited, onToggle } = useToggleFavorite()
  const isFacilityFavorited = isFavorited(facility.id)

  const reserve = React.useCallback(
    async (programId: ProgramId, timetableId: TimetableId) => {
      setOpen(true)

      const close = async () => {
        if (router.asPath.includes('?')) {
          const path = router.asPath.split('?')[0]
          await router.push(path ?? '', undefined, {
            scroll: false,
            shallow: true,
          })
        }

        setOpen(false)
      }

      // User is logged out, show snackbar with login link
      if (!authUser || !isLoggedIn) {
        // Remove query from path
        const path = router.asPath.split('?')[0]
        openSnackbar({
          message: '予約するにはログインが必要です',
          action: {
            label: 'ログインする',
            href: `/login?path=${encodeURIComponent(
              `${path}?programId=${programId}&timetableId=${timetableId}`,
            )}`,
          },
        })

        close()
        return
      }

      // User has not verified email yet
      if (!isEmailVerified) {
        openSnackbar({
          message: '予約するにはメールアドレスの認証が必要です',
          action: {
            label: 'メールを再送',
            onClick: resendVerificationEmail,
          },
        })

        close()
        return
      }

      // User is logged in but profile is not registered, show snackbar with profile link
      if (!isProfileRegistered) {
        openSnackbar({
          message: '予約するにはプロフィール登録が必要です',
          action: {
            label: 'プロフィールを登録する',
            href: `/me/profile`,
          },
        })

        close()
        return
      }

      // Check if timetable is reservable
      try {
        setLoadingTimetableId(timetableId)
        await client.user.timetables.isReservable(timetableId)
        setLoadingTimetableId(undefined)
      } catch (e: unknown) {
        setLoadingTimetableId(undefined)

        if (e instanceof Error) {
          openSnackbar({
            message: 'エラーが発生しました',
            extra: e.message,
            variant: 'error',
          })
        }

        close()
        return
      }

      // User is logged in and can reserve, open reservation modal
      const programPath = buildProgramLink(facility, { id: programId }).as
      const programImage = getProgramImage?.(programId)

      openModal(ReservationModal, {
        expandOnSmallDevices: true,
        isFavorited: isFacilityFavorited,
        programId,
        timetableId,
        programLink: {
          href: programPath,
          target: '_blank',
        },
        programPageUrl: `${siteUrl}${programPath}`,
        programImage: programImage ? getImageUrl(programImage) : OGP_IMAGE_URL,
        pointPurchaseLink: {
          href: generatePointPurchaseUrl(retUrl),
        },
        shortagePointPurchaseLink: {
          href: generateShortagePointPurchaseUrl(
            programId,
            timetableId,
            retUrl,
          ),
        },
        canPurchaseShortage: authUser.canPurchaseShortage,
        onSubmit: async ({ timetable, favoriteFacility }) => {
          try {
            const { reservationId } = await client.user.reservations.create(
              timetableId,
            )

            // Push reservation event to gtm
            pushReservationEvent(authUser, {
              facilityId: facility.id,
              programId,
              timetableId,
              point: timetable.point,
              reservationId,
              referral,
            })

            // Report the conversion to contx and clear its cart
            pushContxCvEvent(authUser.email, [
              {
                id: String(programId),
                name: timetable.program.name,
                price: String(timetable.point),
                count: '1',
              },
            ])

            // Revalidate user data to update points
            await revalidate()

            if (!isFacilityFavorited && favoriteFacility) {
              await onToggle(facility.id)
            }

            await onReserve?.(timetableId)

            openSnackbar({
              message: '予約が完了しました',
              variant: 'success',
              action: {
                label: '確認する',
                href: `/me/reservations`,
                external: true,
              },
            })
          } catch {
            openSnackbar({
              message: '予約できませんでした',
              variant: 'error',
            })
          }
        },
        onClose: () => {
          return close()
        },
      })
    },
    [
      authUser,
      isLoggedIn,
      isEmailVerified,
      isProfileRegistered,
      openModal,
      isFacilityFavorited,
      facility,
      retUrl,
      router,
      openSnackbar,
      resendVerificationEmail,
      client.user.timetables,
      client.user.reservations,
      referral,
      revalidate,
      onReserve,
      onToggle,
      getProgramImage,
    ],
  )

  const openReservationModal = React.useCallback(
    async (programId: ProgramId, timetableId: TimetableId) => {
      if (!programId || !timetableId) return
      if (router.asPath.includes('?')) return

      await router.push(
        `${router.asPath}?programId=${programId}&timetableId=${timetableId}`,
        undefined,
        { scroll: false, shallow: true },
      )
    },
    [router],
  )

  // Show reservation/snackbars on page load
  React.useEffect(() => {
    if (isOpen) return
    if (!programId || !timetableId) return

    reserve(programId, timetableId)
  }, [reserve, isOpen, programId, timetableId])

  // Show snackbar after point purchase
  React.useEffect(() => {
    if (!purchased) return

    openSnackbar({
      message: 'ポイントを購入しました',
      variant: 'success',
    })

    // Remove purchased query from url, but preserve others
    const path = router.asPath.split('?')[0]
    router.replace(
      `${path}?programId=${programId}&timetableId=${timetableId}`,
      undefined,
      { scroll: false, shallow: true },
    )
  }, [purchased, openSnackbar, router, programId, timetableId])

  return { loadingTimetableId, openReservationModal }
}
